import { getViewerType } from "lib/actions/assets/getViewerType";
import { popToastError } from "lib/actions/toast";
import { useAssetsControls } from "lib/hooks/assets/useAssetsControls";
import { useCesium } from "components/elements/CesiumViewer/core/useCesium";
import { useSyncCesium } from "components/elements/CesiumViewer/core/useSyncCesium";
import { KeyframeViewer } from "lib/contexts/ProjectContexts/SelectedViewerContext";
import {
  ProjectLayerModel,
  ProjectLayerRenderAssetModel,
} from "lib/types/layers/ProjectLayerModel";
import { RenderableAsset } from "lib/types/assets/RenderableAsset";

export function makeRenderableFromRenderAsset(
  asset: ProjectLayerRenderAssetModel,
  projectId: number,
): RenderableAsset {
  return {
    contentUri: asset.contentUri,
    vectorContentUri: asset.vectorContentUri,
    asset: {
      // A layer's render assets are addressed by content URI, not by asset id.
      // Zero is deliberately not a real asset id, so these never match an
      // asset-id lookup such as hideAllByAssetId.
      id: 0,
      projectId,
      type: asset.assetType,
    },
  };
}

export function getCesiumViewableVersions(
  layer: ProjectLayerModel,
): RenderableAsset[] {
  return (layer.renderAssets ?? [])
    .filter(
      (asset) => getViewerType(asset.contentUri) === KeyframeViewer.Cesium,
    )
    .map((asset) => makeRenderableFromRenderAsset(asset, layer.projectId));
}

export function useLayersControls() {
  const { assetsPlugin } = useCesium();
  const { flyToAsset } = useAssetsControls();
  const layerVisibility = useSyncCesium(assetsPlugin.layers);

  function isLayerShowing(layer: ProjectLayerModel): boolean {
    return layerVisibility.has(layer.id);
  }

  async function showLayerAssets(layer: ProjectLayerModel): Promise<boolean> {
    try {
      await Promise.all(
        getCesiumViewableVersions(layer).map((v) =>
          assetsPlugin.showViaLayer(v, layer.id),
        ),
      );
      assetsPlugin.showLayer(layer.id);
      return true;
    } catch (error) {
      console.error(
        `Failed to load layer content. LayerId: ${layer.id}, ProjectId: ${layer.projectId}`,
        error,
      );
      hideLayerAssets(layer);
      popToastError(
        "Layer content could not be loaded. Please contact support if this issue persists.",
      );
      return false;
    }
  }

  function hideLayerAssets(layer: ProjectLayerModel): void {
    getCesiumViewableVersions(layer).forEach((v) =>
      assetsPlugin.hideViaLayer(v.contentUri, layer.id),
    );
    assetsPlugin.hideLayer(layer.id);
  }

  function hideRemovedLayerAssets(
    layer: ProjectLayerModel,
    prevRenderAssets: ProjectLayerRenderAssetModel[],
  ): void {
    const newUris = new Set(
      (layer.renderAssets ?? []).map((ra) => ra.contentUri),
    );
    const removedLayer = {
      ...layer,
      renderAssets: prevRenderAssets.filter(
        (ra) => !newUris.has(ra.contentUri),
      ),
    };
    getCesiumViewableVersions(removedLayer).forEach((v) =>
      assetsPlugin.hideViaLayer(v.contentUri, layer.id),
    );
  }

  function toggleLayerAssets(layer: ProjectLayerModel): void {
    if (isLayerShowing(layer)) hideLayerAssets(layer);
    else showLayerAssets(layer);
  }

  /**
   * Shows the layer, then flies the camera to it. `onContentLoaded` runs once
   * the content has loaded, just before the camera starts to move, so a caller
   * can show that it is waiting on a large layer.
   */
  async function flyToLayer(
    layer: ProjectLayerModel,
    onContentLoaded?: () => void,
  ): Promise<void> {
    const isShown = await showLayerAssets(layer);
    if (!isShown) return;
    onContentLoaded?.();
    const versions = getCesiumViewableVersions(layer);
    if (!versions.length) return;
    await flyToAsset(versions[0]);
  }

  return {
    isLayerShowing,
    showLayerAssets,
    hideLayerAssets,
    hideRemovedLayerAssets,
    toggleLayerAssets,
    flyToLayer,
  };
}
